import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getChampionsByYear, deleteChampion, regenerateAllChampions, regenerateChampionsForYear } from '../../services/championService';
import { getAgeGroups } from '../../services/ageGroupService';
import { getSportsCategories } from '../../services/competitionService';
import { getActiveSportsYear, getSportsYears } from '../../services/sportsYearService';
import { Champion, AgeGroup, SportsCategory } from '../../types/models';
import { format } from 'date-fns';
import { Trophy, Search, Medal, Download, FileSpreadsheet, FileText, File as FilePdf, Trash2, ChevronLeft, ChevronRight, X, Award, RefreshCw } from 'lucide-react';
import { exportChampionsToExcel, exportChampionsToCSV, exportChampionsToPDF } from '../../utils/export';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';

const ITEMS_PER_PAGE = 10;

interface ChampionWin {
  event_name: string;
  category_name: string;
  points: number;
}

const ChampionsList: React.FC = () => {
  const [champions, setChampions] = useState<Champion[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [categories, setCategories] = useState<SportsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string>('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateMenu, setShowRegenerateMenu] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState<string | null>(null);
  const { t, i18n } = useTranslation('common');
  const { user } = useAuthStore();
  
  // State for champion details modal
  const [selectedChampion, setSelectedChampion] = useState<Champion | null>(null);
  const [championWins, setChampionWins] = useState<ChampionWin[]>([]);
  const [loadingWins, setLoadingWins] = useState(false);

  // Years dropdown: populated from sports_years (admin-managed), with a sensible fallback.
  const yearsArray = availableYears.length > 0
    ? availableYears
    : Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    (async () => {
      try {
        const [years, active] = await Promise.all([getSportsYears(), getActiveSportsYear()]);
        if (years.length > 0) {
          setAvailableYears(years.map((y) => y.year));
        }
        setSelectedYear(active);
      } catch (err) {
        console.error('Error loading sports years:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const checkUserRoles = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', user.id);

      setIsAdmin(userRoles?.some(ur => ur.roles.role_name === 'admin') ?? false);
    };

    checkUserRoles();
  }, [user]);

  useEffect(() => {
    fetchData();
    
    // Set up real-time subscription for champions table
    const championsSubscription = supabase
      .channel('champions_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'champions' 
      }, () => {
        console.log('Champions table changed, refreshing data...');
        fetchData();
      })
      .subscribe();
    
    // Set up real-time subscription for competition results
    const resultsSubscription = supabase
      .channel('competition_results_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'competition_results' 
      }, () => {
        console.log('Results table changed, refreshing champions data...');
        fetchData();
      })
      .subscribe();
      
    // Set up real-time subscription for team results
    const teamResultsSubscription = supabase
      .channel('team_results_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'team_results' 
      }, () => {
        console.log('Team results table changed, refreshing champions data...');
        fetchData();
      })
      .subscribe();
    
    // Clean up subscriptions on unmount
    return () => {
      championsSubscription.unsubscribe();
      resultsSubscription.unsubscribe();
      teamResultsSubscription.unsubscribe();
    };
  }, [selectedYear, selectedGender]);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [championsData, ageGroupsData, categoriesData] = await Promise.all([
        getChampionsByYear(selectedYear, selectedGender === 'All' ? undefined : selectedGender),
        getAgeGroups(),
        getSportsCategories()
      ]);
      setChampions(championsData);
      setAgeGroups(ageGroupsData);
      setCategories(categoriesData);
      setError(null);
      setRefreshing(false);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load champions data');
      setRefreshing(false);
    } finally {
      setLoading(false);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategories, selectedAgeGroup, selectedCategory]);

  const getAgeGroup = (dateOfBirth: string) => {
    const birthYear = new Date(dateOfBirth).getFullYear();
    return ageGroups.find(group => group.birth_years.includes(birthYear));
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this champion?')) {
      return;
    }

    try {
      setError(null);
      await deleteChampion(id);
      setChampions(prev => prev.filter(champion => champion.champion_id !== id));
    } catch (err: any) {
      console.error('Error deleting champion:', err);
      setError(err.message || 'Failed to delete champion');
    }
  };

  const fetchChampionWins = async (championId: string, studentId: string) => {
    setLoadingWins(true);
    try {
      // Fetch all first place wins for this student in the selected year
      const { data, error } = await supabase
        .from('competition_results')
        .select(`
          result_id,
          position,
          points_earned,
          event:events(
            event_id,
            event_name,
            category:sports_categories(
              category_id,
              category_name
            )
          )
        `)
        .eq('student_id', studentId)
        .eq('competition_year', selectedYear)
        .eq('position', 1)
        .order('points_earned', { ascending: false });

      if (error) throw error;

      // Format the data for display
      const wins = data?.map(result => ({
        event_name: result.event.event_name,
        category_name: result.event.category.category_name,
        points: result.points_earned
      })) || [];

      setChampionWins(wins);
    } catch (error) {
      console.error('Error fetching champion wins:', error);
      setChampionWins([]);
    } finally {
      setLoadingWins(false);
    }
  };

  const handleChampionClick = async (champion: Champion) => {
    setSelectedChampion(champion);
    if (champion.student_id) {
      await fetchChampionWins(champion.champion_id, champion.student_id);
    }
  };

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleRegenerateChampions = async (forAllYears = false) => {
    try {
      setRegenerating(true);
      setError(null);
      setRegenerateSuccess(null);
      
      let result;
      if (forAllYears) {
        result = await regenerateAllChampions();
      } else {
        result = await regenerateChampionsForYear(selectedYear);
      }
      
      if (result.success) {
        setRegenerateSuccess(result.message);
        // Refresh data after regeneration
        fetchData();
      } else {
        setError(`Failed to regenerate champions: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error regenerating champions:', error);
      setError(error.message || 'Failed to regenerate champions');
    } finally {
      setRegenerating(false);
      setShowRegenerateMenu(false);
    }
  };

  const filteredChampions = champions.filter(champion => {
    const matchesSearch = 
      `${champion.student?.first_name} ${champion.student?.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (champion.student?.first_name_tamil || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (champion.student?.last_name_tamil || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (champion.student?.house?.house_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategories = 
      selectedCategories === 'All' || 
      champion.categories_won.toString() === selectedCategories;

    const studentAgeGroup = champion.student?.date_of_birth ? 
      getAgeGroup(champion.student.date_of_birth)?.age_group_id : null;
    
    const matchesAgeGroup = 
      selectedAgeGroup === 'All' || 
      studentAgeGroup === selectedAgeGroup;

    // Check if the champion has won in the selected category
    const matchesCategory = selectedCategory === 'All';
    // Note: We would need to fetch the actual categories won by each champion
    // This would require additional backend support to track which categories each champion won

    return matchesSearch && matchesCategories && matchesAgeGroup && matchesCategory;
  });

  const totalPages = Math.ceil(filteredChampions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedChampions = filteredChampions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    switch (format) {
      case 'excel':
        exportChampionsToExcel(filteredChampions);
        break;
      case 'csv':
        exportChampionsToCSV(filteredChampions);
        break;
      case 'pdf':
        exportChampionsToPDF(filteredChampions);
        break;
    }
    setShowExportMenu(false);
  };

  // Helper function to get translated text based on current language
  const getTranslatedText = (en: string, nn: string, ta: string): string => {
    if (i18n.language === 'ta') return ta;
    if (i18n.language === 'nn') return nn;
    return en;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('champions.title')}</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          
          {isAdmin && (
            <div className="relative">
              <button
                onClick={() => setShowRegenerateMenu(!showRegenerateMenu)}
                disabled={regenerating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Trophy className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating...' : 'Regenerate Champions'}
              </button>
              
              {showRegenerateMenu && (
                <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu">
                    <button
                      onClick={() => handleRegenerateChampions(false)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Trophy className="h-4 w-4 mr-2" />
                      Regenerate for {selectedYear}
                    </button>
                    <button
                      onClick={() => handleRegenerateChampions(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate for all years
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('champions.actions.export')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('champions.export.toExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('champions.export.toCSV')}
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FilePdf className="h-4 w-4 mr-2" />
                    {t('champions.export.toPDF')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {regenerateSuccess && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-700">{regenerateSuccess}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('champions.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {yearsArray.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('champions.filters.allGenders')}</option>
                <option value="Male">{t('champions.filters.male')}</option>
                <option value="Female">{t('champions.filters.female')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedCategories}
                onChange={(e) => setSelectedCategories(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('champions.filters.allCategories')}</option>
                <option value="3">{t('champions.filters.categories.three')}</option>
                <option value="4">{t('champions.filters.categories.four')}</option>
                <option value="5">{t('champions.filters.categories.five')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedAgeGroup}
                onChange={(e) => setSelectedAgeGroup(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('champions.filters.allAgeGroups')}</option>
                {ageGroups.map(group => (
                  <option key={group.age_group_id} value={group.age_group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('champions.filters.allCategories')}</option>
                {categories.map(category => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredChampions.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="mx-auto h-12 w-12 text-amber-500" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              {t('champions.noChampions.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedCategories !== 'All' || selectedAgeGroup !== 'All' || selectedCategory !== 'All'
                ? t('champions.noChampions.description.withFilters')
                : t('champions.noChampions.description.noFilters')}
            </p>
            <p className="mt-4 text-sm text-blue-600">
              Champions must have at least 3 first place wins to be listed here.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.name')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.tamilName')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.house')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.ageGroup')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.categoriesWon')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('champions.table.totalPoints')}
                    </th>
                    {isAdmin && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedChampions.map((champion) => {
                    const ageGroup = champion.student?.date_of_birth ? 
                      getAgeGroup(champion.student.date_of_birth) : null;
                      
                    return (
                      <tr key={champion.champion_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleChampionClick(champion)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          >
                            {champion.student?.first_name} {champion.student?.last_name}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {champion.student?.first_name_tamil} {champion.student?.last_name_tamil}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="h-3 w-3 rounded-full mr-2" 
                              style={{ backgroundColor: champion.student?.house?.house_color }}
                            />
                            <div className="text-sm text-gray-900">
                              {champion.student?.house?.house_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {ageGroup ? ageGroup.group_name : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Medal className="h-4 w-4 text-amber-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {champion.categories_won}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {champion.total_points} {t('competitions.details.sections.details.points')}
                          </div>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleDelete(champion.champion_id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(startIndex + ITEMS_PER_PAGE, filteredChampions.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredChampions.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Champion Details Modal */}
      {selectedChampion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center">
                <Trophy className="h-6 w-6 text-amber-500 mr-3" />
                <h2 className="text-xl font-bold text-gray-900">
                  {i18n.language === 'ta' && selectedChampion.student?.first_name_tamil && selectedChampion.student?.last_name_tamil
                    ? `${selectedChampion.student.first_name_tamil} ${selectedChampion.student.last_name_tamil}`
                    : `${selectedChampion.student?.first_name} ${selectedChampion.student?.last_name}`}
                </h2>
              </div>
              <button
                onClick={() => setSelectedChampion(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">
                    {getTranslatedText(
                      'Champion Details',
                      'Mesterdetaljer',
                      'சாம்பியன் விவரங்கள்'
                    )}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {getTranslatedText('Year:', 'År:', 'ஆண்டு:')}
                      </span>
                      <span className="font-medium">{selectedChampion.competition_year}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {getTranslatedText('Gender:', 'Kjønn:', 'பாலினம்:')}
                      </span>
                      <span className="font-medium">
                        {selectedChampion.gender === 'M' 
                          ? getTranslatedText('Male', 'Gutt', 'ஆண்') 
                          : getTranslatedText('Female', 'Jente', 'பெண்')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {getTranslatedText('Categories Won:', 'Kategorier vunnet:', 'வென்ற பிரிவுகள்:')}
                      </span>
                      <span className="font-medium">{selectedChampion.categories_won}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {getTranslatedText('Total Points:', 'Totalt poeng:', 'மொத்த புள்ளிகள்:')}
                      </span>
                      <span className="font-medium">{selectedChampion.total_points}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-amber-800 mb-2">
                    {getTranslatedText(
                      'House Information',
                      'Husinformasjon',
                      'இல்ல தகவல்'
                    )}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {getTranslatedText('House:', 'Hus:', 'இல்லம்:')}
                      </span>
                      <div className="flex items-center">
                        <div 
                          className="h-3 w-3 rounded-full mr-2" 
                          style={{ backgroundColor: selectedChampion.student?.house?.house_color }}
                        />
                        <span className="font-medium">{selectedChampion.student?.house?.house_name}</span>
                      </div>
                    </div>
                    {selectedChampion.student?.date_of_birth && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {getTranslatedText('Birth Year:', 'Fødselsår:', 'பிறந்த ஆண்டு:')}
                        </span>
                        <span className="font-medium">
                          {new Date(selectedChampion.student.date_of_birth).getFullYear()}
                        </span>
                      </div>
                    )}
                    {selectedChampion.student?.date_of_birth && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {getTranslatedText('Age Group:', 'Aldersgruppe:', 'வயது பிரிவு:')}
                        </span>
                        <span className="font-medium">
                          {getAgeGroup(selectedChampion.student.date_of_birth)?.group_name || '-'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Award className="h-5 w-5 text-amber-500 mr-2" />
                  {getTranslatedText(
                    'First Place Wins',
                    'Førsteplasseringer',
                    'முதல் இட வெற்றிகள்'
                  )}
                </h3>
                
                {loadingWins ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : championWins.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">
                      {getTranslatedText(
                        'No wins found for this champion.',
                        'Ingen seire funnet for denne mesteren.',
                        'இந்த சாம்பியனுக்கு வெற்றிகள் எதுவும் கிடைக்கவில்லை.'
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {getTranslatedText('Event', 'Hendelse', 'நிகழ்வு')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {getTranslatedText('Category', 'Kategori', 'பிரிவு')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {getTranslatedText('Points', 'Poeng', 'புள்ளிகள்')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {championWins.map((win, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {win.event_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {win.category_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                {win.points}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedChampion(null)}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {getTranslatedText('Close', 'Lukk', 'மூடு')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChampionsList;